import instance from '../xhrService';
import { getOfflineTenantFeatures } from '../../db/db';
import { withOfflineFallback } from '../offlineFallback';

export interface Tenant {
  id?: number;
  name: string;
  domain: string;
}

export interface Division {
  id: number;
  name: string;
  createdAt?: Date;
}

export type HubspotApiTokenStatus = {
  masked: string;
  last4: string;
  createdAt: string;
  expiresAt: string;
  lastUsedAt: string | null;
  expired: boolean;
};

export type HubspotApiSettings = {
  enabled: boolean;
  token: HubspotApiTokenStatus | null;
};

const tenantApi = {
  index(): Promise<Tenant[]> {
    return instance.get(`/api/tenants`).then((res) => res.data);
  },
  create(tenant: Tenant): Promise<Tenant> {
    return instance.post(`/api/tenants`, tenant).then((res) => res.data);
  },
  update(tenant: Tenant): Promise<Tenant> {
    return instance
      .put(`/api/tenants/${tenant.id}`, tenant)
      .then((res) => res.data);
  },
  delete(tenant: Tenant): Promise<Tenant> {
    return instance.delete(`/api/tenants/${tenant.id}`).then((res) => res.data);
  },
  show(tenant: Tenant): Promise<Tenant> {
    return instance.get(`/api/tenants/${tenant.id}`).then((res) => res.data);
  },

  divisions: {
    index(tenantId: number): Promise<Division[]> {
      return instance
        .get(`/api/tenants/${tenantId}/divisions`)
        .then((res) => res.data);
    },
    getDivisionsForTenants(tenantIds: number[]): Promise<Division[]> {
      return instance
        .get(`/api/tenants/divisions`, {
          params: { tenantIds },
        })
        .then((res) => res.data);
    },
  },
  regulations: {
    index(tenantId: number): Promise<Division[]> {
      return instance
        .get(`/api/tenants/${tenantId}/regulations`)
        .then((res) => res.data);
    },
  },

  features: {
    index(tenantId: number, type?: string): Promise<any> {
      const query = type ? `type=${type}` : '';

      return instance
        .get(`/api/tenants/${tenantId}/features?${query}`)
        .then((res) => res.data);
    },

    update(
      tenantId: number,
      featureId: number,
      isEnabled: boolean,
    ): Promise<any> {
      return instance
        .put(`/api/tenants/${tenantId}/updateTenantFeatures/${featureId}`, {
          is_enabled: isEnabled,
        })
        .then((res) => res.data);
    },

    getTenantFeature(): Promise<any> {
      // tenantHasFeature() returns false until this query resolves, so offline
      // every feature-gated control vanished. Cached at download time.
      return withOfflineFallback(
        () => instance.get('/api/tenants/features').then((res) => res.data),
        getOfflineTenantFeatures,
      );
    },

    getAllFeaturesName(): Promise<any> {
      return instance.get('/api/featuresName').then((res) => res.data);
    },
  },

  // SW2-2187 — endpoints take no id; the backend acts on the JWT's tenant
  hubspot: {
    getSettings(): Promise<HubspotApiSettings> {
      return instance.get('/api/tenants/hubspot-api').then((res) => res.data);
    },

    updateSettings(data: { enabled: boolean }): Promise<HubspotApiSettings> {
      return instance
        .put('/api/tenants/hubspot-api', data)
        .then((res) => res.data);
    },

    /** Plaintext token is returned in this response only — never persist it */
    generateToken(): Promise<{ token: string; status?: HubspotApiSettings }> {
      return instance
        .post('/api/tenants/hubspot-api/token')
        .then((res) => res.data);
    },

    revokeToken(): Promise<any> {
      return instance
        .delete('/api/tenants/hubspot-api/token')
        .then((res) => res.data);
    },

    /** Force a full re-sync of the tenant's HubSpot data table. */
    resync(): Promise<{ ok: boolean; projectsQueued: number }> {
      return instance
        .post('/api/tenants/hubspot-api/resync')
        .then((res) => res.data);
    },
  },

  whitelist: {
    getCompanyLogo(tenantId: number): Promise<any> {
      return instance
        .get(`/api/tenants/${tenantId}/getCompanyLogo`)
        .then((response) => response.data)
        .catch((e) => {
          console.log(e.message);

          return Promise.reject(false);
        });
    },
    uploadTenantLogo: ({
      tenantId,
      formData,
    }: {
      tenantId: number;
      formData: any;
    }): Promise<any> => {
      if (!tenantId || !formData) {
        return Promise.reject(false);
      }

      return instance
        .post(`/api/tenants/${tenantId}/uploadTenantLogo`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then((response) => response.data)
        .catch((e) => {
          console.log(e.message);

          return Promise.reject(false);
        });
    },
    deleteCompanyLogo: (tenantId: number): Promise<any> => {
      return instance
        .delete(`/api/tenants/${tenantId}/deleteCompanyLogo`)
        .then((response) => response.data)
        .catch((e) => {
          console.log(e.message);

          return Promise.reject(false);
        });
    },
  },

  payment: {
    getPaymentMethod(tenantId: number): Promise<any> {
      return instance
        .get(`/api/tenants/${tenantId}/getPaymentMethod`)
        .then((response) => response.data)
        .catch((e) => {
          console.log(e.message);

          return Promise.reject(false);
        });
    },
    editPaymentMethod: (tenantId: number, paymentMethod: any): Promise<any> => {
      return instance
        .put(`/api/tenants/${tenantId}/editPaymentMethod`, paymentMethod)
        .then((response) => response.data)
        .catch((e) => {
          console.log(e.message);

          return Promise.reject(false);
        });
    },
  },
  mfa: {
    getMfaEnabled(tenantId: number): Promise<any> {
      return instance
        .get(`/api/tenants/${tenantId}/getMfaEnabled`)
        .then((response) => response.data)
        .catch((e) => {
          console.log(e.message);

          return Promise.reject(false);
        });
    },
    editMfaEnabled: (tenantId: number, mfaEnabled: any): Promise<any> => {
      return instance
        .put(`/api/tenants/${tenantId}/editMfaEnabled`, mfaEnabled)
        .then((response) => response.data)
        .catch((e) => {
          console.log(e.message);

          return Promise.reject(false);
        });
    },
  },
  pdfEditting: {
    getAppendOnlyEnabled(tenantId: number): Promise<any> {
      return instance
        .get(`/api/tenants/${tenantId}/append-only-enabled`)
        .then((response) => response.data)
        .catch((e) => {
          console.log(e.message);

          return Promise.reject(false);
        });
    },
    editAppendOnlyEnabled: (
      tenantId: number,
      appendOnlyEnabled: any,
    ): Promise<any> => {
      return instance
        .put(
          `/api/tenants/${tenantId}/edit-append-only-enabled`,
          appendOnlyEnabled,
        )
        .then((response) => response.data)
        .catch((e) => {
          console.log(e.message);

          return Promise.reject(false);
        });
    },
    editLockAppendOnly: (
      tenantId: number,
      lockAppendOnly: any,
    ): Promise<any> => {
      return instance
        .put(`/api/tenants/${tenantId}/edit-lock-append-only`, lockAppendOnly)
        .then((response) => response.data)
        .catch((e) => {
          console.log(e.message);

          return Promise.reject(false);
        });
    },
  },
  authorizationAgreement: {
    getSettings(
      tenantId: number,
    ): Promise<{ agreement_body_template: string }> {
      return instance
        .get(`/api/tenants/${tenantId}/authorization-agreement-settings`)
        .then((res) => res.data);
    },

    updateSettings(
      tenantId: number,
      data: { agreement_body_template?: string | null },
    ): Promise<{ agreement_body_template: string }> {
      return instance
        .put(`/api/tenants/${tenantId}/authorization-agreement-settings`, data)
        .then((res) => res.data);
    },
  },

  updateMaxInspectionTemplates(
    tenantId: number,
    maxTemplates: number,
  ): Promise<any> {
    return instance
      .put(`/api/tenants/${tenantId}/max-inspection-templates`, {
        max_templates: maxTemplates,
      })
      .then((res) => res.data);
  },
  fetchIsAppendOnly: async (
    tenantId: number,
    projectId?: number,
  ): Promise<boolean> => {
    return instance
      .get(`/api/tenants/${tenantId}/isAppendOnly`, {
        params: { projectId },
      })
      .then((response) => response.data.isAppendOnly);
  },
};

export default tenantApi;
